/**
 * Client-side Google Tag Manager container boot.
 *
 * The single injection path for `gtm.js`. Two callers share it so the
 * container can never double-load:
 *
 *   - `<GtmEagerBoot />` (via `<GtmRouteScope />`) boots it on mount for the
 *     marketing/auth/onboarding/billing route groups where pageview + ad
 *     attribution is wanted eagerly.
 *   - `pushToDataLayer` / `pushToDataLayerAndWait` (`lib/site/gtm.ts`) boot
 *     it lazily the moment a `brew_*` conversion event fires on a route
 *     that does NOT mount GTM (e.g. the upgrade-limit dialog or first-email
 *     activation inside the workspace), so conversion attribution survives
 *     the route gating.
 *
 * GTM processes every message already queued on `window.dataLayer` when the
 * container boots, so events pushed before the script loads are not lost —
 * including the Consent Mode v2 defaults that `<ConsentDefault />` pushes
 * synchronously during HTML parsing on every route. That parse-time ordering
 * (consent first, `gtm.start` after) is exactly what Google's canonical
 * snippet produces, so the lazy path keeps Consent Mode v2 semantics intact.
 */

import { GTM_CONTAINER_ID, isGtmEnabled } from '@/lib/site/gtm-config'

declare global {
  interface Window {
    google_tag_manager?: Record<string, unknown>
  }
}

/** DOM id of the injected script tag; the idempotency guard. */
export const GTM_SCRIPT_ELEMENT_ID = 'brew-gtm-container'

/**
 * Inject `gtm.js` if the container is enabled and not already present.
 * Idempotent and safe to call from any client code path; no-ops on the
 * server, when GTM is disabled for the deployment (`window.__brewGtmEnabled`
 * is the server-authoritative marker), or when the container id is not a
 * `GTM-` id.
 *
 * `GTM_CONTAINER_ID` is read defensively: unit tests mock
 * `@/lib/site/gtm-config` with only `isGtmEnabled`, and a preview build can
 * legitimately resolve an empty id.
 */
export function ensureGtmBooted(): void {
  if (typeof window === 'undefined') {
    return
  }
  if (!isGtmEnabled()) {
    return
  }
  const containerId: unknown = GTM_CONTAINER_ID
  if (typeof containerId !== 'string' || !containerId.startsWith('GTM-')) {
    return
  }
  if (window.google_tag_manager?.[containerId]) {
    return
  }
  if (document.getElementById(GTM_SCRIPT_ELEMENT_ID)) {
    return
  }

  // Mirrors Google's canonical snippet: mark the boot on the dataLayer,
  // then load the container script asynchronously.
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.id = GTM_SCRIPT_ELEMENT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`
  document.head.appendChild(script)
}
